import React, { useEffect, useMemo } from 'react'
import { Options } from '@popperjs/core'
import { CSSProperties } from 'styled-components'
import { CardProps } from '../card'
import { Position } from './types'
import { TooltipProvider } from './TooltipContext'
import { Tip } from './Tip'
import { usePopper } from './usePopper'

export interface TooltipProps {
  cardProps?: CardProps
  children: React.ReactNode
  className?: string
  containerStyles?: CSSProperties
  'data-test-id'?: string
  delay?: number
  disabled?: boolean
  followingCursor?: boolean
  interactive?: boolean
  offset?: [number | string, number | string]
  place?: Position
  popperOptions?: Partial<Options>
  tip: string | React.ReactNode
  zIndex?: number
}

export const TooltipInner = ({
  place = 'top',
  tip = '',
  children,
  disabled = false,
  offset,
  delay = 0,
  cardProps,
  popperOptions,
  interactive = true,
  className,
  followingCursor = false,
  containerStyles,
  ...other
}: TooltipProps) => {
  const { 'data-test-id': dataTestId } = other
  const options = useMemo<Options>(
    () => ({
      placement: place,
      strategy: 'absolute',
      ...(popperOptions ?? {}),
      modifiers: [
        ...(popperOptions?.modifiers || []),
        {
          name: 'offset',
          options: {
            offset: offset ?? [0, 2],
          },
        },
      ],
    }),
    [offset, place, popperOptions]
  )

  const { containerRef, tooltipRef, instance } = usePopper({
    options,
    disabled,
    delay,
    interactive,
    followingCursor,
  })

  useEffect(() => {
    instance.current?.update()
  }, [tip, instance])

  return (
    <span
      className={className}
      style={{ position: 'relative', ...containerStyles }}
      ref={containerRef}
      data-test-id={`Tooltip-Container-${dataTestId}`}
    >
      {children}
      {!disabled && tip && (
        <Tip
          tip={tip}
          cardProps={cardProps}
          ref={tooltipRef}
          interactive={interactive}
          {...other}
        />
      )}
    </span>
  )
}

/**
 * @deprecated - If it doesn't contain interactive content, use `Tooltip` from
 *   `@chilipiper/design-system/src/new` instead, otherwise don't migrate
 */
export const Tooltip = (props: TooltipProps) => {
  return (
    <TooltipProvider>
      <TooltipInner {...props} />
    </TooltipProvider>
  )
}
